import {
  Unarchive as SavedIcon,
  Remove as RemoveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Star as StarIcon
} from '@mui/icons-material';
import {
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Typography,
  Box,
  IconButton,
  Button,
  Chip,
  Tooltip
} from '@mui/material';
import React from 'react';

import { SavedForLaterItemProps } from './types';

const SavedForLaterItem: React.FC<SavedForLaterItemProps> = ({
  item,
  onUpdateQuantity,
  onRemove,
  onMoveToCart
}) => {
  const formatFullAddress = (addressFull?: any) => {
    if (!addressFull) return '';
    const { street_address, city, state, zip_code, country } = addressFull;
    const parts = [street_address, city, state, zip_code].filter(Boolean);
    if (country && country !== 'US') parts.push(country);
    return parts.join(', ');
  };

  return (
    <ListItem
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        mb: 1,
        bgcolor: 'grey.50'
      }}
    >
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: 'grey.400' }}>
          <SavedIcon />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2">
              {item.service_name || `Service ${item.service_id}`}
            </Typography>
            {item.product_class && (
              <Chip
                label={`Type ${item.product_class}`}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
        }
        secondary={
          <Box>
            <Typography variant="body2" color="text.secondary">
              {item.provider_name || 'Provider'}
            </Typography>
            {item.selected_address_label && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <LocationIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Tooltip title={formatFullAddress(item.selected_address_full)} arrow>
                  <Typography variant="caption" color="text.secondary">
                    {item.selected_address_label}
                  </Typography>
                </Tooltip>
              </Box>
            )}
            {item.selected_perk_name && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <StarIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                <Typography variant="caption" color="text.secondary">
                  {item.selected_perk_name}
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Chip
                label="Saved for Later"
                size="small"
                color="default"
              />
              {item.added_catalog_instance_id && (
                <Chip
                  label={item.added_catalog_instance_id === 'aaaaaaaa-bbbb-1111-2222-aaaaaaaaaaaa' ? 'Main Catalog' : 'Partner Catalog'}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem', height: '20px' }}
                />
              )}
            </Box>
          </Box>
        }
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
            disabled={item.quantity <= 1}
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
          <Typography variant="body2" sx={{ minWidth: 20, textAlign: 'center' }}>
            {item.quantity}
          </Typography>
          <IconButton
            size="small"
            onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => onMoveToCart(item.id)}
            sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}
          >
            Move to Cart
          </Button>
          <IconButton
            size="small"
            color="error"
            onClick={() => onRemove(item.id)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </ListItem>
  );
};

export default SavedForLaterItem;